import {NoRowsOverlay} from '@components/table/NoRows.overlay';
import {Checkbox, IconButton, LabelDisplayedRowsArgs, Stack, Tooltip} from '@mui/material';
import {Refresh} from '@mui/icons-material';
import {
  GridCellParams,
  GridColDef,
  GridFeatureMode,
  GridLocaleText,
  GridRowEditStartParams,
  GridRowEditStartReasons,
  GridRowEditStopParams,
  GridRowEditStopReasons,
  GridRowHeightParams,
  GridRowHeightReturnValue,
  GridRowModel,
  GridRowParams,
  GridRowsProp,
  GridSortItem,
  GridValidRowModel,
  MuiEvent,
  useGridApiRef
} from '@mui/x-data-grid-pro';
import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';

import {NewTableStyled, TableMenuStyle} from './NewTable.styled';

import {Colors} from '@constants/colors.constants';
import {TableConfig} from '@constants/table.constants';
import {RadioChecked, RadioLess, RadioUnchecked} from '@icons/index';
import {GridStatePro} from '@mui/x-data-grid-pro/models/gridStatePro';
import {GridRowClassNameParams} from '@mui/x-data-grid/models/params';

interface TableComponentProps {
  getRowClassName?: (params: GridRowClassNameParams<any>) => string;
  columns?: GridColDef[];
  data?: GridRowsProp;
  disableEdition?: boolean;
  disablePagination?: boolean;
  disableRowSelection?: boolean;
  disableRowSelectionOnClick?: boolean;
  handlePageChange?: (page: number) => void;
  handlePageSizeChange?: (pageSize: number) => void;
  handleRowChanged?: (row: any, oldRow: GridRowModel) => GridValidRowModel;
  handleRowSelected?: (rows: any[]) => void;
  handleSortModelChange?: (newModel: GridSortItem) => void;
  loading?: boolean;
  page?: number;
  pageSize?: number;
  paginationMode?: GridFeatureMode;
  rowsSelected?: any[];
  singleRowSelection?: boolean;
  sortingMode?: GridFeatureMode;
  sortModel?: GridSortItem[];
  totalRows?: number;
  autoHeight?: boolean;
  setIsEditing?: (value: boolean) => void;
  isRowSelectable?: (params: GridRowParams) => boolean;
  editableFields?: string[];
  state?: GridStatePro;
  handleStateChange?: (state: GridStatePro) => void;
  fullScreen?: boolean;
  columnThreshold?: number;
  isCellEditable?: (params: GridCellParams) => boolean;
  getRowHeight?: (params: GridRowHeightParams) => GridRowHeightReturnValue | 'auto';
  handleReload?: () => void;
}

const TableComponent = (props: TableComponentProps) => {
  const {
    columns = [],
    data = [],
    disablePagination,
    disableRowSelection,
    disableEdition,
    handlePageChange,
    handlePageSizeChange,
    handleRowChanged,
    handleRowSelected,
    handleSortModelChange,
    loading,
    page,
    pageSize,
    paginationMode = 'server',
    rowsSelected = [],
    singleRowSelection,
    sortingMode = 'server',
    sortModel,
    totalRows,
    disableRowSelectionOnClick = false,
    autoHeight,
    setIsEditing,
    getRowClassName,
    isRowSelectable,
    editableFields,
    fullScreen,
    isCellEditable,
    columnThreshold = 3,
    getRowHeight,
    handleReload,
  } = props;

  const {t} = useTranslation();

  const apiRef = useGridApiRef();

  const [columnSizes, setColumnSizes] = React.useState<any>({});

  const handleKeyDown = useCallback((params, event) => {
    if (event.key !== 'Tab') return;

    event.preventDefault();
    event.stopPropagation();

    const api = apiRef.current;

    const editableColumns = api.getAllColumns()
      .filter(col => col.editable)
      .map(col => col.field);

    const currentField = params.field;
    const currentIndex = editableColumns.indexOf(currentField);

    const isLastColumn = currentIndex === editableColumns.length - 1;

    const rowIds = api.getSortedRowIds(); // fonctionne avec MUI v6
    const rowIndex = rowIds.indexOf(params.id);

    let nextRowId = params.id;
    let nextField;

    if (isLastColumn) {
      const isLastRow = rowIndex === rowIds.length - 1;

      nextRowId = isLastRow ? rowIds[0] : rowIds[rowIndex + 1];
      nextField = editableColumns[0];

      if (isLastRow) {
        api.stopRowEditMode({id: rowIds[rowIndex]});
        return;
      }

      api.setCellFocus(nextRowId, nextField);
      api.stopRowEditMode({id: rowIds[rowIndex]});
      api.startRowEditMode({id: nextRowId});
    } else {
      nextField = editableColumns[currentIndex + 1];
      api.setCellFocus(nextRowId, nextField);
    }
  }, []);


  return (
    <Stack flex={1} width={fullScreen ? '100%' : undefined} sx={{
      position: 'relative',
      '& .grey-row': {
        backgroundColor: Colors.lightRed,
      }
    }}>
      {handleReload && (
        <Tooltip title={t('global.reload')}>
          <IconButton
            size="small"
            onClick={handleReload}
            aria-label="reload"
            sx={{position: 'absolute', bottom: 8, left: 8, zIndex: 2}}>
            <Refresh fontSize="small"/>
          </IconButton>
        </Tooltip>
      )}
      <NewTableStyled
        apiRef={apiRef}
        loading={loading}
        className={singleRowSelection ? 'single-row-selection' : undefined}
        disableColumnMenu
        disableColumnFilter
        isCellEditable={isCellEditable}
        onColumnWidthChange={() => apiRef.current.exportState()}
        onRowEditStart={(params: GridRowEditStartParams, event: MuiEvent<any>) => {
          if (disableEdition) {
            event.defaultMuiPrevented = true;
            return;
          }
          if (params.reason === GridRowEditStartReasons.deleteKeyDown ||
            (params.reason === GridRowEditStartReasons.printableKeyDown && event.keyCode !== 69)) {
            event.defaultMuiPrevented = true;
          }

          if (params.field && editableFields && !editableFields.includes(params.field)) {
            event.defaultMuiPrevented = true;
            if (setIsEditing) {
              setIsEditing(true);
            }
            apiRef.current.startRowEditMode({id: params.id, fieldToFocus: editableFields[0]});
          }

          if (setIsEditing) {
            setIsEditing(true);
          }
        }}
        onRowEditStop={(params: GridRowEditStopParams, event: MuiEvent) => {
          if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            event.defaultMuiPrevented = false;
          }
          if (setIsEditing) {
            setIsEditing(false);
          }
        }}
        hideFooterRowCount={disablePagination}
        hideFooter={disablePagination && disableRowSelection}
        pinnedColumns={{right: ['actions']}}
        columnHeaderHeight={TableConfig.headerHeight}
        rowHeight={TableConfig.rowHeight}
        pageSizeOptions={[10, 20, TableConfig.pagination, 100, 250]}
        onPaginationModelChange={(model) => {
          if (JSON.stringify(model) !== JSON.stringify({page: page ?? 0, pageSize: pageSize ?? 50})) {
            handlePageSizeChange?.(model.pageSize);
            handlePageChange?.(model.page);
          }
        }}
        isRowSelectable={isRowSelectable}
        paginationModel={{pageSize: pageSize ?? 50, page: page ?? 1}}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        autoHeight={autoHeight}
        components={{
          BaseCheckbox: React.forwardRef((params, ref) => (
            <Checkbox
              {...params}
              ref={ref}
              checkedIcon={<RadioChecked/>}
              indeterminateIcon={<RadioLess/>}
              icon={<RadioUnchecked/>}
              sx={params.disabled ? {display: 'none'} : undefined}
            />
          )),
          NoResultsOverlay: () => <NoRowsOverlay/>,
          NoRowsOverlay: () => <NoRowsOverlay/>,
        }}
        componentsProps={{
          basePopper: {
            sx: TableMenuStyle,
          },
        }}
        getRowClassName={getRowClassName}
        editMode="row"
        processRowUpdate={handleRowChanged}
        onProcessRowUpdateError={(err) => console.log(err)}
        sortingMode={sortingMode}
        sortingOrder={['asc', 'desc']}
        onSortModelChange={(value) => (value.length ? handleSortModelChange?.(value[0]!) : undefined)}
        sortModel={sortModel ? sortModel : undefined}
        columns={columns.map(c => {
          const width = columnSizes[c.field];
          if (width) {
            console.log(c.field);
            c.width = width;
          }
          return c;
        })}
        checkboxSelection={!disableRowSelection}
        keepNonExistentRowsSelected
        onRowSelectionModelChange={(selected) => {
          if (singleRowSelection) handleRowSelected?.(selected.length ? selected.slice(selected.length - 1) : []);
          else handleRowSelected?.(selected);
        }}
        onColumnResize={(params) => {
          columnSizes[params.colDef.field] = params.width;
          setColumnSizes(columnSizes);
          console.log(params.colDef.field, params.width);
        }}
        rowSelectionModel={rowsSelected}
        rows={data}
        localeText={{
          footerRowSelected: (count: number) => t('table.rowSelected', {count}),
          footerTotalRows: '',
          MuiTablePagination: {
            labelDisplayedRows: (paginationInfo: LabelDisplayedRowsArgs) => t('table.displayedRows', {...paginationInfo}),
          },
        } as Partial<GridLocaleText>}
        rowCount={totalRows}
        paginationMode={paginationMode}
        pagination={!disablePagination}
        columnThreshold={columnThreshold}
        getRowHeight={getRowHeight}
        onCellKeyDown={handleKeyDown}
      />
    </Stack>
  );
};

export default TableComponent;
